import {
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Radio,
  RadioChangeEvent,
  Row
} from 'antd'
import { FormInstance } from 'antd/lib'
import { useState } from 'react'

import { requiredRules } from 'src/app-constants'
import { Text } from 'src/components'
import { useLocales } from 'src/locales'

import { useStyles } from '../styles'

type Props = {
  form: FormInstance
  isPrimary: boolean
}

export const ConsultationFormUrinarySection = ({ form, isPrimary }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const [isPositive, setIsPositive] = useState(false)

  const order = isPrimary ? 30 : 25

  const onChange = (e: RadioChangeEvent) => {
    const value = e.target.value

    const isPositiveUnilateral = String(value).includes('uni')
    const isPositiveBilateral = value === 'positive_bi'

    setIsPositive(isPositiveUnilateral)

    form.setFieldsValue({
      form: {
        physicalExaminationSection: {
          urinarySystemAssessment: {
            tappingSymptom: {
              isPositiveBilateral: isPositiveUnilateral
                ? false
                : isPositiveBilateral,
              isPositiveUnilateral: {
                isSelected: isPositiveUnilateral,
                value: undefined
              }
            }
          }
        }
      }
    })
  }

  return (
    <Col span={24}>
      <Text>
        {order}. {t('consultation_form.urinary.title')}
      </Text>

      <Row gutter={[16, 16]} className="mt-16 ml-16">
        <Col span={24}>
          <Text>
            {order}.1 {t('consultation_form.urinary.urination')}
          </Text>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'urination',
              'isFree'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.isFree')}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'urination',
              'isDifficult'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.isDifficult')}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'urination',
              'isPainful'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.isPainful')}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'urination',
              'isNotPainful'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.isNotPainful')}</Checkbox>
          </Form.Item>
        </Col>

        <Divider className={styles.divider} />

        <Col span={24}>
          <Form.Item
            name={'tapping_symptom'}
            label={`${order}.2 ${t('consultation_form.urinary.tapping_symptom')}`}
            rules={requiredRules}
          >
            <Radio.Group onChange={onChange}>
              <Radio value="negative_bi">
                {t('consultation_form.urinary.isNegativeBilateral')}
              </Radio>
              <Radio value="positive_bi">
                {t('consultation_form.urinary.isPositiveBilateral')}
              </Radio>
              <Radio value="positive_uni">
                {t('consultation_form.urinary.isPositiveUnilateral')}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        {isPositive && (
          <Col span={24}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'urinarySystemAssessment',
                'tappingSymptom',
                'isPositiveUnilateral',
                'value'
              ]}
              rules={requiredRules}
            >
              <Input.TextArea placeholder={t('consultation_form.to_specify')} />
            </Form.Item>
          </Col>
        )}

        <Divider className={styles.divider} />

        <Col span={24}>
          <Text>
            {order}.3 {t('consultation_form.urinary.defecation')}
          </Text>
        </Col>

        <Col span={8}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'defecationCharacteristics',
              'hasConstipation'
            ]}
            valuePropName="checked"
          >
            <Checkbox>
              {t('consultation_form.urinary.hasConstipation')}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'defecationCharacteristics',
              'hasNoPeculiarities'
            ]}
            valuePropName="checked"
          >
            <Checkbox>
              {t('consultation_form.urinary.hasNoPeculiarities')}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'defecationCharacteristics',
              'hasDiarrhea'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.hasDiarrhea')}</Checkbox>
          </Form.Item>
        </Col>

        <Divider className={styles.divider} />

        <Col span={24}>
          <Text>
            {order}.4 {t('consultation_form.urinary.fecal')}
          </Text>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'fecalMassCharacteristics',
              'isSemiLiquid'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.isSemiLiquid')}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'fecalMassCharacteristics',
              'isLiquid'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.isLiquid')}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'fecalMassCharacteristics',
              'isFormed'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.isFormed')}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'fecalMassCharacteristics',
              'hasBloodStreaks'
            ]}
            valuePropName="checked"
          >
            <Checkbox>
              {t('consultation_form.urinary.hasBloodStreaks')}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'fecalMassCharacteristics',
              'hasMucus'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.hasMucus')}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'fecalMassCharacteristics',
              'hasPus'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.hasPus')}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'urinarySystemAssessment',
              'fecalMassCharacteristics',
              'isNormal'
            ]}
            valuePropName="checked"
          >
            <Checkbox>{t('consultation_form.urinary.isNormal')}</Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </Col>
  )
}
